import { ApiError } from '../../../api/errors';
import { permissions as route42Permissions } from '../../../auth/permissions';
import { getBilling } from '../../billing/api/billing.api';
import { getConversation, listConversations } from '../../conversations/api/conversations.api';
import { getDocument, listMachineDocuments } from '../../documents/api/documents.api';
import { getInvestigation, listInvestigations } from '../../investigations/api/investigations.api';
import { getMachineWorkspace } from '../../machine-memory/api/machine-memory.api';
import { listMachines } from '../../machines/api/machines.api';
import {
  APPLICATION_GUIDANCE_COUNT_CAP,
  type GuidanceSafeState,
  buildApplicationContextHint,
  summarizeBilling,
  summarizeConversation,
  summarizeConversations,
  summarizeDocument,
  summarizeDocuments,
  summarizeInvestigation,
  summarizeInvestigations,
  summarizeMachineMemory,
} from './application-context';

export interface CollectNextBestActionStateInput {
  activeCustomerId: string;
  machineCount?: number | null;
  machineId: string | null;
  pathname: string;
  permissions: ReadonlySet<string>;
}

/**
 * Collect only bounded aggregate state needed by the deterministic 21.8 engine.
 * This runs on demand for "What should I do next?" rather than continuously
 * pulling unrelated application data into the Product Guidance surface.
 */
export async function collectNextBestActionSafeState(input: CollectNextBestActionStateInput): Promise<GuidanceSafeState> {
  const safeState: GuidanceSafeState = {};
  const contextHint = buildApplicationContextHint({
    pathname: input.pathname,
    organizationSelected: Boolean(input.activeCustomerId),
  });
  const routeId = contextHint.route?.id ?? null;
  const conversationId = contextHint.scope.conversation?.id ?? null;
  const investigationId = contextHint.scope.investigation?.id ?? null;
  const documentId = contextHint.scope.document?.id ?? null;

  const machineTotal = input.machineCount === null || input.machineCount === undefined
    ? await countMachines()
    : Math.min(APPLICATION_GUIDANCE_COUNT_CAP, Math.max(0, Math.floor(input.machineCount)));
  if (machineTotal !== null) safeState.machines = { total: machineTotal };

  const tasks: Array<Promise<void>> = [];
  if (input.machineId && input.permissions.has(route42Permissions.documentsRead)) {
    tasks.push(
      listMachineDocuments(input.machineId)
        .then((result) => {
          safeState.documents = summarizeDocuments(result.documents);
        })
        .catch(() => undefined),
    );
  }

  if (input.machineId && input.permissions.has(route42Permissions.conversationRead)) {
    tasks.push(
      listConversations(input.machineId)
        .then((result) => {
          const visibleCount = result.conversations.filter((item) => Number(item.turn_count ?? 0) > 0).length;
          safeState.conversations = summarizeConversations(visibleCount);
        })
        .catch(() => undefined),
    );
  }

  if (input.machineId && input.permissions.has(route42Permissions.investigationRead)) {
    tasks.push(
      listInvestigations(input.machineId)
        .then((result) => {
          safeState.investigations = summarizeInvestigations(result.investigations);
        })
        .catch(() => undefined),
    );
  }

  if (routeId === 'conversation' && conversationId && input.permissions.has(route42Permissions.conversationRead)) {
    tasks.push(
      getConversation(conversationId)
        .then((result) => {
          safeState.conversation = summarizeConversation(result.conversation, result.turn_count ?? result.turns.length);
        })
        .catch(() => undefined),
    );
  }

  if (routeId === 'investigation' && investigationId && input.permissions.has(route42Permissions.investigationRead)) {
    tasks.push(
      getInvestigation(investigationId)
        .then((result) => {
          safeState.investigation = summarizeInvestigation(result);
        })
        .catch(() => undefined),
    );
  }

  if (routeId === 'document' && documentId && input.permissions.has(route42Permissions.documentsRead)) {
    tasks.push(
      getDocument(documentId)
        .then((result) => {
          safeState.document = summarizeDocument(result);
        })
        .catch(() => undefined),
    );
  }

  if (input.activeCustomerId && input.permissions.has(route42Permissions.billingRead)) {
    tasks.push(
      getBilling(input.activeCustomerId)
        .then((billing) => {
          safeState.billing = summarizeBilling(billing);
        })
        .catch(() => undefined),
    );
  }

  await Promise.all(tasks);

  if (input.machineId && input.permissions.has(route42Permissions.machineRead)) {
    try {
      const workspace = await getMachineWorkspace(input.machineId);
      safeState.machine_memory = summarizeMachineMemory(workspace, normalizeMemoryStatus(workspace.machine_memory.status));
    } catch (value) {
      safeState.machine_memory = {
        status: value instanceof ApiError && value.code === 'MACHINE_MEMORY_NOT_READY' ? 'NOT_READY' : 'UNAVAILABLE',
        knowledge_gaps: 0,
        conflicts: 0,
      };
    }
  }

  return safeState;
}

async function countMachines(): Promise<number | null> {
  try {
    let total = 0;
    let cursor: string | null = null;
    do {
      const result = await listMachines({ limit: 50, cursor });
      total = Math.min(APPLICATION_GUIDANCE_COUNT_CAP, total + result.machines.length);
      cursor = total >= APPLICATION_GUIDANCE_COUNT_CAP ? null : result.nextCursor;
    } while (cursor);
    return total;
  } catch {
    return null;
  }
}

function normalizeMemoryStatus(value: string | null | undefined): NonNullable<GuidanceSafeState['machine_memory']>['status'] {
  const normalized = String(value ?? '').trim().toUpperCase();

  // Machine Memory service completion states such as GENERATED,
  // GENERATED_WITH_WARNINGS, GENERATED_WITH_LIMITED_KNOWLEDGE, and ACTIVE
  // are usable/current snapshots. Do not confuse GENERATED with GENERATING.
  if (
    normalized === 'ACTIVE'
    || normalized === 'ALREADY_CURRENT'
    || normalized === 'GENERATED'
    || normalized.startsWith('GENERATED_')
  ) return 'READY';

  if (
    normalized.includes('UPDAT')
    || normalized.includes('GENERATING')
    || normalized.includes('PROCESS')
    || normalized.includes('BUILD')
  ) return 'UPDATING';
  if (normalized.includes('NOT_READY') || normalized.includes('PENDING')) return 'NOT_READY';
  if (normalized.includes('UNAVAILABLE') || normalized.includes('FAILED') || normalized.includes('ERROR')) return 'UNAVAILABLE';
  return 'READY';
}
